import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import ccmService from '../../services/ccmService';
import './Assessments.css';

const Assessments = ({ enrollmentId }) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, [enrollmentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tplRes, histRes] = await Promise.all([
        ccmService.getAssessmentTemplates(),
        ccmService.getPatientAssessments(enrollmentId)
      ]);
      
      if(tplRes.success) setTemplates(tplRes.data);
      if(histRes.success) setHistory(histRes.data);
      
    } catch (err) {
      console.error(err);
      setError('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = (template) => {
    setSelectedTemplate(template);
    setResponses({});
    setSuccess('');
    setError('');
  };

  const handleInputChange = (questionId, value) => {
    setResponses({
      ...responses,
      [questionId]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Format responses back to array as expected by our mock backend
    const formattedResponses = selectedTemplate.questions.map(q => ({
      questionId: q.id,
      question: q.question,
      answer: responses[q.id] || ''
    }));

    try {
      setSubmitting(true);
      const res = await ccmService.submitAssessment({
        enrollment_id: enrollmentId,
        assessment_id: selectedTemplate.id,
        responses: formattedResponses
      });

      if(res.success) {
        setSuccess(`Assessment submitted! Generated Risk Level: ${res.data.acuity_level}`);
        setSelectedTemplate(null);
        fetchData(); // Refresh history
      } else {
        setError(res.message);
      }
    } catch(err) {
      setError('An error occurred submitting the assessment');
    } finally {
      setSubmitting(false);
    }
  };

  if(loading) return <div className="p-5 text-center"><Spinner animation="border" /></div>;

  return (
    <div className="assessments-container">
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {!selectedTemplate ? (
        <div className="fade-in">
          <h4 className="mb-4 fw-bold">Health Risk Assessments (HRA)</h4>
          
          <div className="available-templates mb-5">
            <h5 className="text-muted mb-3">Available Templates</h5>
            <div className="d-flex gap-3 flex-wrap">
              {templates.length === 0 && <p>No templates available.</p>}
              {templates.map(tpl => (
                <Card key={tpl.id} className="template-card flex-fill shadow-sm border-0" onClick={() => handleStartAssessment(tpl)}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="fw-bold text-primary mb-1">{tpl.name}</h6>
                        <small className="text-muted">{tpl.description}</small>
                      </div>
                      <Button variant="light" className="rounded-circle btn-icon"><i className="bi bi-play-fill text-primary"></i></Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>

          <div className="assessment-history">
            <h5 className="text-muted mb-3">Assessment History</h5>
            {history.length === 0 ? (
               <Card className="bg-light border-0"><Card.Body className="text-center text-muted">No prior assessments documented for this enrollment.</Card.Body></Card>
            ) : (
              history.map(h => (
                <Card key={h.id} className="mb-3 border-0 shadow-sm">
                  <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">{h.assessment_name}</h6>
                      <small className="text-muted">Completed: {new Date(h.completed_at).toLocaleDateString()} by {h.performed_by_name}</small>
                    </div>
                    <Badge bg={h.acuity_level === 'High' ? 'danger' : h.acuity_level === 'Medium' ? 'warning' : 'success'} className="p-2">
                       Acuity: {h.acuity_level}
                    </Badge>
                  </Card.Body>
                </Card>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="active-assessment fade-in">
           <Button variant="link" className="text-decoration-none p-0 mb-3" onClick={() => setSelectedTemplate(null)}>
              <i className="bi bi-arrow-left me-1"></i> Back to HRA Overview
           </Button>
           
           <Card className="border-0 shadow-lg">
             <Card.Header className="bg-white border-0 pt-4 pb-0">
               <h4 className="fw-bold text-primary">{selectedTemplate.name}</h4>
               <p className="text-muted">{selectedTemplate.description}</p>
             </Card.Header>
             <Card.Body className="p-4">
               <Form onSubmit={handleSubmit}>
                 {selectedTemplate.questions.map((q, idx) => (
                   <Form.Group key={q.id} className="mb-4 question-group p-3 rounded">
                     <Form.Label className="fw-bold">{idx + 1}. {q.question} {q.required && <span className="text-danger">*</span>}</Form.Label>
                     
                     {q.type === 'multiple_choice' ? (
                       <div className="mt-2 d-flex gap-3">
                         {q.options.map(opt => (
                           <Form.Check 
                             key={opt}
                             type="radio" 
                             id={`${q.id}-${opt}`}
                             label={opt} 
                             name={q.id}
                             required={q.required}
                             checked={responses[q.id] === opt}
                             onChange={(e) => handleInputChange(q.id, e.target.value)}
                             value={opt}
                           />
                         ))}
                       </div>
                     ) : (
                       <Form.Control 
                         as="textarea"
                         rows={2}
                         className="mt-2 premium-text-input"
                         required={q.required}
                         value={responses[q.id] || ''}
                         onChange={(e) => handleInputChange(q.id, e.target.value)}
                       />
                     )}
                   </Form.Group>
                 ))}
                 
                 <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                   <Button variant="primary" type="submit" disabled={submitting} className="premium-btn px-4">
                     {submitting ? <Spinner size="sm" /> : 'Sign & Submit Assessment'}
                   </Button>
                 </div>
               </Form>
             </Card.Body>
           </Card>
        </div>
      )}
    </div>
  );
};

export default Assessments;
